import { BaseApiService } from './baseApiService';
import { environment } from '../../config/environment';

export interface SessionPhoto {
  id: number; session_id: number; width: number; height: number;
  created_on: string; url: string; thumbnail_url: string;
}
export interface PhotoPairing { grant_id: number; token: string; expires_at: string }
export interface PhotoVisit { session_id: number; visit_date: string; photo_count: number }
export interface PhoneGrant {
  upload_token: string; session_id: number; visit_date: string; expires_at: string;
  photo_limit?: number; photo_count?: number;
}
export const SESSION_PHOTO_LIMIT = 15;

class SessionPhotoService extends BaseApiService {
  async list(sessionId: number) {
    return (await this.fetchWithAuth<{ data: SessionPhoto[] }>(`/session/${sessionId}/photos/`)).data;
  }
  async previousVisits(sessionId: number) {
    return (await this.fetchWithAuth<{ data: PhotoVisit[] }>(`/session/${sessionId}/photos/previous-visits/`)).data;
  }
  async listForCompare(sessionId: number, currentSessionId: number) {
    return (await this.fetchWithAuth<{ data: SessionPhoto[] }>(`/session/${sessionId}/photos/?compare_from=${currentSessionId}`)).data;
  }
  async pair(sessionId: number) {
    return (await this.fetchWithAuth<{ data: PhotoPairing }>(`/session/${sessionId}/photos/grant/`, { method: 'POST' })).data;
  }
  async revoke(sessionId: number) {
    await this.fetchWithAuth(`/session/${sessionId}/photos/grant/`, { method: 'DELETE' });
  }
  async remove(sessionId: number, photoId: number) {
    await this.fetchWithAuth(`/session/${sessionId}/photos/${photoId}/`, { method: 'DELETE' });
  }
}
export const sessionPhotoService = new SessionPhotoService();

// Public upload-only transport: never reads EHR credentials, refreshes a JWT,
// logs a response, or redirects to login.
export async function exchangePhotoPairing(token: string): Promise<PhoneGrant> {
  const response = await fetch(`${environment.API_URL}/photo-upload/pair/`, {
    method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error('This QR code expired or was already used. Scan a new code from the visit.');
  return (await response.json()).data;
}

export function uploadSessionPhoto(grant: PhoneGrant, file: File, uploadId: string,
  progress: (percentage: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${environment.API_URL}/photo-upload/`);
    request.setRequestHeader('Authorization', `PhotoUpload ${grant.upload_token}`);
    request.timeout = 120000;
    request.upload.onprogress = event => { if (event.lengthComputable) progress(Math.round(event.loaded / event.total * 100)); };
    request.onerror = () => reject(new Error('Upload interrupted. Retry this photo.'));
    request.ontimeout = () => reject(new Error('Upload timed out. Retry this photo.'));
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) { resolve(); return; }
      const message = request.status === 403 ? 'Pairing expired or revoked. Scan a new QR code.'
        : request.status === 400 ? `Photo rejected. Check the format, 20 MiB size limit and ${grant.photo_limit ?? SESSION_PHOTO_LIMIT}-photo visit limit.`
        : 'Upload failed. Retry this photo.';
      reject(new Error(message));
    };
    const body = new FormData();
    body.append('upload_id', uploadId); body.append('photo', file);
    request.send(body);
  });
}
